import React, { useState } from 'react'
import {
  Box,
  Button,
  ButtonBase,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { CATEGORIES } from './catalogue'
import { toneOf } from './liveUi'
import { liveIcon } from './liveIcons'

// Presenting-complaint tiles. Multi-select; `existing` categories are shown
// ticked and can't be removed here. onConfirm(newKeysInOrder).
const CategoryGrid = ({
  existing = [],
  onConfirm,
  onCancel,
  title = 'What are you attending?',
  confirmLabel = 'Start',
}) => {
  const [picked, setPicked] = useState([])

  const toggle = (key) =>
    setPicked((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    )

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        <Typography variant="h5" fontWeight={800}>
          {title}
        </Typography>
        <Typography color="text.secondary">
          Pick one or more - the first is the primary category. You can add more
          later.
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 1.5,
        }}
      >
        {CATEGORIES.map((category) => {
          const tone = toneOf(category.tone)
          const already = existing.includes(category.key)
          const order = picked.indexOf(category.key)
          const selected = already || order >= 0
          const Icon = liveIcon(category.icon, { categoryKey: category.key })
          return (
            <ButtonBase
              key={category.key}
              disabled={already}
              onClick={() => toggle(category.key)}
              sx={{
                minHeight: 112,
                borderRadius: 3,
                p: 2,
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                textAlign: 'left',
                border: 3,
                borderColor: selected ? tone.main : 'transparent',
                bgcolor: selected ? tone.main : tone.bg,
                color: selected ? '#fff' : tone.dark,
                boxShadow: 1,
                opacity: already ? 0.7 : 1,
                transition: 'background-color 120ms ease',
              }}
            >
              <Stack direction="row" gap={1.5} width="100%">
                {Icon && <SvgIcon component={Icon} sx={{ fontSize: 36 }} />}
                <Stack flex={1} gap={0.5}>
                  <Typography
                    fontSize="1.25rem"
                    fontWeight={800}
                    lineHeight={1.2}
                  >
                    {category.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: selected
                        ? 'rgba(255,255,255,0.9)'
                        : 'text.secondary',
                    }}
                  >
                    {category.description}
                  </Typography>
                </Stack>
                {selected && (
                  <Stack alignItems="center">
                    <CheckCircleIcon />
                    {order === 0 && !existing.length && (
                      <Typography variant="caption" fontWeight={700}>
                        Primary
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            </ButtonBase>
          )
        })}
      </Box>
      <Stack direction="row" gap={1} justifyContent="flex-end">
        {onCancel && (
          <Button size="large" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          size="large"
          variant="contained"
          color="error"
          disabled={!picked.length}
          onClick={() => onConfirm(picked)}
          sx={{
            minHeight: 56,
            minWidth: 180,
            fontSize: '1.1rem',
            fontWeight: 800,
          }}
        >
          {confirmLabel}
        </Button>
      </Stack>
    </Stack>
  )
}

export default CategoryGrid
